import type { Lang } from "../../lib/sprache";
import { t } from "../../lib/sprache";
import { contentStand, type HubKoop, type Stand } from "../agentur/hub/hub-model";
import type { CContract, CreatorBase, CSigner } from "./data";
import { activeDealIds, creatorRules, monthDone, monthNeedsInvoice, monthPill, talentStateContext, viewTalentInvoice, type MonthContext, type TalentInvoiceView } from "./model";

/**
 * „Meine Marken“ im Creator-Portal: dieselbe Hub-Ansicht wie in der Agentur, aber nur mit dem, was der Creator ohnehin
 * sieht (talent_deals, talent_contracts, eigene Rechnungen, talent_deal_deliverables). Kampagnen sind für Creator nicht
 * lesbar (keine Spalte in talent_deals), daher gibt es nur den Blickwinkel „Nach Marke“. Reine Funktionen, getestet in
 * apps/web/test/hub-model.test.ts.
 */

const stand = (art: Stand["art"], label: string, ziel: string | null = null): Stand => ({ art, label, ziel });
const RANG: Record<Stand["art"], number> = { haengt: 0, laeuft: 1, ok: 2, leer: 3 };

/**
 * Vertrag aus Sicht des Creators: vollständig unterschrieben ✓; „Bitte unterschreiben“, bei der Marke bzw. bei der Agentur
 * oder angehalten ◷; abgelehnt oder ohne nötige Unterschrift der Eltern !; kein (versendeter) Vertrag –. Entwürfe sieht der
 * Creator nicht (talent_contracts ab Versand), aufgehobene zählen nicht.
 */
export function creatorVertragsStand(vertraege: readonly CContract[], signers: readonly CSigner[]): Stand {
  const wirksam = vertraege.filter((c) => c.status !== "void");
  if (!wirksam.length) return stand("leer", t("Kein Vertrag", "No contract"));
  const staende = wirksam.map((c): Stand => {
    const ziel = `/creator/vertraege/${c.id}`;
    const meine = signers.filter((s) => s.contract_id === c.id);
    if (c.status === "signed") return stand("ok", t("Unterschrieben", "Signed"), ziel);
    if (c.declined_at || meine.some((s) => s.declined)) return stand("haengt", t("Abgelehnt", "Declined"), ziel);
    if (c.guardian_required) return stand("haengt", t("Eltern fehlen", "Parents missing"), ziel);
    if (c.deal_cancelled) return stand("laeuft", t("Angehalten", "On hold"), ziel);
    const ich = meine.find((s) => s.role === "talent" && (s.capacity ?? "party") === "party");
    if (c.talent_signs && !ich?.signed) return stand("laeuft", t("Bitte unterschreiben", "Please sign"), ziel);
    const marke = meine.some((s) => s.role === "brand" && !s.signed);
    return stand("laeuft", marke ? t("Bei Marke", "With brand") : t("Bei Agentur", "With agency"), ziel);
  });
  const erster = [...staende].sort((a, b) => RANG[a.art] - RANG[b.art])[0]!;
  return { ...erster, ziel: wirksam.length === 1 ? erster.ziel : staende.filter((s) => s.art === "haengt").length === 1 ? erster.ziel : null };
}

/**
 * Rechnung aus Sicht des Creators je Kooperation, aus den Monaten wie unter „Deine Deals“ (monthPill, monthDone): alle Monate
 * erledigt ✓; ein Monat mit Rechnung, Anforderung oder fälliger Rechnung ◷ (Stand des Monats, z. B. „Im Zahlungslauf“);
 * ein roter Monatsstand (Korrektur, überfällig) !; K-594: hat die Marke einen Monat bezahlt, die Rechnung des Creators ist aber
 * weder angefordert noch da, ◷ „Marke hat bezahlt“ (talent_deal_periods.status „paid“; die Sicht zeigt das nur, wenn die Agentur
 * dem Creator den Zahlungsstand der Marke zeigt, sonst steht dort „invoiced“); sonst –.
 */
export function creatorRechnungsStand(d: { deal: CreatorBase["deals"][number]; periods: CreatorBase["periods"]; invoices: CreatorBase["invoices"]; today: string; lang: Lang; ctx: MonthContext }): Stand {
  const monate = d.periods.filter((p) => p.deal_id === d.deal.id && p.status !== "cancelled").sort((a, b) => a.month.localeCompare(b.month));
  if (!monate.length) return stand("leer", t("Noch keine", "None yet"));
  const offen = monate.filter((p) => !monthDone(p, d.deal, d.invoices, d.today));
  if (!offen.length) return stand("ok", t("Erledigt", "Done"));
  const mitStand = offen.map((p) => {
    const eigene = d.invoices.some((i) => i.period_id === p.id && !["withdrawn", "discarded", "cancelled"].includes(i.status));
    const relevant = eigene || !!p.invoice_requested_at || monthNeedsInvoice(p, d.deal, d.invoices, d.today);
    return { p, relevant, pill: monthPill(p, d.deal, d.invoices, d.lang, d.ctx) };
  });
  const rot = mitStand.find((m) => m.pill.tone === "error");
  if (rot) return stand("haengt", rot.pill.label);
  const erster = mitStand.find((m) => m.relevant);
  if (erster) return stand("laeuft", erster.pill.label);
  // K-594: statt „Noch keine“, wenn die Marke schon bezahlt hat (bei direkter Abrechnung zahlt die Marke die eigene Rechnung)
  if (d.deal.invoice_route !== "direct_to_brand" && mitStand.some((m) => m.p.status === "paid")) return stand("laeuft", t("Marke hat bezahlt", "Brand has paid"));
  return monate.length > offen.length ? stand("laeuft", t(`${monate.length - offen.length}/${monate.length} Monate erledigt`, `${monate.length - offen.length}/${monate.length} months done`)) : stand("leer", t("Noch keine", "None yet"));
}

/** Kooperationen des Creators als Zeilen des Hubs (eine Agentur, das eigene Profil). */
export function creatorHubKoops(b: CreatorBase, vertraege: { contracts: readonly CContract[]; signers: readonly CSigner[] } | null | undefined, today: string, lang: Lang): HubKoop[] {
  const rules = creatorRules(b.defaults, b.org);
  const sctx = talentStateContext(today, rules, b.org.currency);
  const views = new Map(b.invoices.map((r) => viewTalentInvoice(r, sctx, b.org.timezone, lang, rules)).filter((v): v is TalentInvoiceView => !!v).map((v) => [v.row.id, v]));
  const ctx: MonthContext = { today, views, selfBilling: !!b.talent.self_billing_agreed_on, ...(b.statusLabels ? { statusLabels: b.statusLabels } : {}) };
  const aktiv = activeDealIds(b.deals, b.periods, b.invoices, today);
  const periods = new Map(b.periods.map((p) => [p.id, p]));
  const name = b.talent.display_name ?? [b.talent.first_name, b.talent.last_name].filter(Boolean).join(" ");
  return b.deals.map((deal) => {
    const marke = b.brands.get(deal.brand_id)?.name ?? t("Marke", "Brand");
    return {
      id: deal.id,
      titel: deal.title,
      href: `/creator/deals/${deal.id}`,
      marke: { id: deal.brand_id, name: marke },
      creator: { id: b.talent.id, name },
      kampagne: null,
      aktiv: aktiv.has(deal.id),
      vertrag: creatorVertragsStand((vertraege?.contracts ?? []).filter((c) => c.deal_id === deal.id), vertraege?.signers ?? []),
      content: contentStand({
        deal, beitraege: b.deliverables.filter((x) => x.deal_id === deal.id), periods, beiMarke: new Set(), today, arbeitsstand: deal.status, basis: null,
      }),
      rechnung: creatorRechnungsStand({ deal, periods: b.periods, invoices: b.invoices, today, lang, ctx }),
      suchtext: `${marke} ${deal.title}`.toLowerCase(),
    };
  });
}
